import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

export const fetchRole = createAsyncThunk(
  "internalUser/fetchRole",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/roles`);
      const data = response.data.map(row => ({ value: row.id, label: row.name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load role");
    }
  }
);

export const getManager = createAsyncThunk(
  "internalUser/getManager",
  async (role_name, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-managers?role_name=${role_name}`);
      const data = response.data.map(row => ({ value: row.id, label: row.name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load role");
    }
  }
);

export const fetchRolePermission = createAsyncThunk(
  "internalUser/fetchRolePermission",
  async (id = 1, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/roles/` + id);
      return response?.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load permission");
    }
  }
);
export const fetchCountryMobileCode = createAsyncThunk(
  "internalUser/fetchCountryMobileCode",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-countries`);
      const data = response.data.map(row => ({ value: row.country_code, label: row.country_code, iso: row.code?.toLowerCase(), name: row.name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load country");
    }
  }
);
export const fetchCountry = createAsyncThunk(
  "internalUser/fetchCountry",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-countries`);
      const data = response.data.map(row => ({ value: row.id, label: row.name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load country");
    }
  }
);
export const fetchCity = createAsyncThunk(
  "internalUser/fetchCity",
  async (country_id, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-cities?country_id=` + country_id);
      const data = response.data.map(row => ({ value: row.id, label: row.name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load city");
    }
  }
);
export const fetchOffice = createAsyncThunk(
  "internalUser/fetchOffice",
  async (city_id, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/fetch-offices?city_id=` + city_id);
      const data = response.data.map(row => ({ value: row.id, label: row.office_name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load office");
    }
  }
);
export const fetchOfficeCity = createAsyncThunk(
  "internalUser/fetchOfficeCity",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-office-city`);
      const data = response.data.map(row => ({ value: row.id, label: row.name }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load office");
    }
  }
);

export const fetchDeliveryPartnerCities = createAsyncThunk(
  "internalUser/fetchDeliveryPartnerCities",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-delivery-partner-cities`);

      const cities = response?.data?.data || response?.data || [];

      const data = cities.map((row) => ({
        value: row.city_id,
        label: row.city_name,
        country_id: row.country_id,
        country_name: row.country_name,
      }));

      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to load delivery partner cities"
      );
    }
  }
);

const MULTIPART_FILE_KEYS = new Set([
  "profile_image",
  "id_proof",
  "residence_image",
  "driving_licence_image",
  "insurance_image",
  "vehicle_certificate_image",
]);

const DELIVERY_PARTNER_FILE_KEYS = [
  "profile_image",
  "residence_image",
  "driving_licence_image",
  "insurance_image",
  "vehicle_certificate_image",
];

function appendDeliveryPartnerFormData(formData, data) {
  Object.entries(data).forEach(([key, value]) => {
    if (DELIVERY_PARTNER_FILE_KEYS.includes(key)) {
      if (value instanceof File) {
        formData.append(key, value);
      }
    } else if (key === "other_documents" && Array.isArray(value)) {
      value.forEach((file) => {
        if (file instanceof File) {
          formData.append("other_documents[]", file);
        }
      });
    } else {
      formData.append(key, value ?? "");
    }
  });
}

export const storeDeliveryPartnerDetails = createAsyncThunk(
  "internalUser/storeDeliveryPartnerDetails",
  async (data, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      appendDeliveryPartnerFormData(formData, data);
      const response = await api.post(
        "admin/store-delivery-partner-details",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to save delivery partner"
      );
    }
  }
);

export const updateDeliveryPartnerDetails = createAsyncThunk(
  "internalUser/updateDeliveryPartnerDetails",
  async (data, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      appendDeliveryPartnerFormData(formData, data);
      const id = data?.id;
      const url = id
        ? `admin/update-delivery-partner-details/${id}`
        : "admin/update-delivery-partner-details";
      const response = await api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to update delivery partner"
      );
    }
  }
);

export const addInternalUser = createAsyncThunk(
  "internalUser/addInternalUser",
  async (data, { rejectWithValue }) => {
    try {
      let formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (MULTIPART_FILE_KEYS.has(key)) {
          if (value instanceof File) {
            formData.append(key, value);
          } else if (value === null || value === "") {
            formData.append(key, "");
          }
          return;
        }
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      });

      let url = "";
      if (data?.id) {
        url += `/update/${data.id}`;
      }

      const response = await api.post("admin/users" + url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to add user");
    }
  }
);

export const getInternalUser = createAsyncThunk(
  "internalUser/getInternalUser",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/users?type=${data.type}&tab=${data.activetab}&page=${data.page}&perPage=${data.perPage}&search=${data.search}&filter=${data.filter}&city_id=${data.city}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to get user");
    }
  }
);

export const deleteInternalUser = createAsyncThunk(
  "internalUser/deleteInternalUser",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.del("admin/users/" + id);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to delete user");
    }
  }
);
export const bulkActionUpdate = createAsyncThunk(
  "customer/bulkActionUpdate",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/bulk-update", data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to perform action");
    }
  }
);

export const approveInternalUser = createAsyncThunk(
  "internalUser/approveInternalUser",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/approve-customer/" + data.id, { status: data.value });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to load user");
    }
  }
);

export const sendUserLoginDetails = createAsyncThunk(
  "internalUser/sendUserLoginDetails",
  async (user_id, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/send-account-creation-mail", { user_id });
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to send");
    }
  }
);

export const fetchDeliveryPartnerOrders = createAsyncThunk(
  "internalUser/fetchDeliveryPartnerOrders",
  async (params, { rejectWithValue }) => {
    try {
      const {
        user_id,
        type = "pending", // pending | completed
        page = 1,
        per_page = 10,
        sort_by = "order_id",
        sort_order = "asc",
      } = params;

      const response = await api.get(
        `admin/fetch-delivery-partner-order?user_id=${user_id}&type=${type}&page=${page}&per_page=${per_page}&sort_by=${sort_by}&sort_order=${sort_order}`
      );

      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch delivery partner orders"
      );
    }
  }
);
// for downloading internal user report
export const downloadInternalUserReport = createAsyncThunk(
  "internalUser/downloadInternalUserReport",
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/download-internal-users-report", params);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to download report.");
    }
  }
);

const internalUsersSlice = createSlice({
  name: "internalUser",
  initialState: {
    list_users: [],
    selected: null,
    roles: [],
    role_permissions: [],
    countries: [],
    cities: [],
    offices: [],
    mobile_country_code: [],
    offices_city: [],
    managers: [],
    status: "idle",
    loading: false,
    list_loading: false,
    delivery_partner_cities: [],
    delivery_orders: [],
    delivery_orders_meta: null,
    delivery_summary: {
      payment_pending: 0,
      deduction_amount: 0,
      completed_jobs: 0,
      pending_jobs: 0,
      progress_percentage: 0,
      total_earned: 0,
    },
  },
  reducers: {
    setSelectedInternalUser(state, action) {
      const payload = action.payload || {};
      const residenceDoc =
        payload.residence_image ?? payload.id_proof ?? payload.residence_image_url;
      state.selected = {
        id: payload.id,
        first_name: payload.first_name,
        last_name: payload.last_name ?? null,
        email: payload.email,
        mobile_number: payload.mobile_number,
        gender: payload.gender,
        alternate_number: payload.alternate_number || null,
        alternate_country_code: payload.alternate_country_code || null,
        country_code: payload.country_code || null,
        designation: payload.designation,
        profile_image: payload.profile_image,
        residence_id: payload.residence_id,
        id_proof: residenceDoc,
        residence_image: residenceDoc,
        permanent_address: payload.kyc_details?.permanent_address ?? null,
        driving_licence: payload.kyc_details?.driving_licence ?? null,
        driving_licence_image: payload.kyc_details?.driving_licence_image ?? null,
        vehicle_number: payload.kyc_details?.vehicle_number ?? null,
        vehicle_certificate_image:
          payload.kyc_details?.vehicle_certificate_image ?? null,
        insurance: payload.kyc_details?.insurance ?? null,
        insurance_image: payload.kyc_details?.insurance_image ?? null,
        role_id: payload.roles?.[0]?.id,
        country_id: payload?.office?.country_id ?? payload?.delivery_location?.country_id,
        city_id: payload?.office?.city_id ?? payload?.delivery_location?.city_id,
        office_id: payload?.office?.id ?? payload.office_id,
        status: payload.status,
        type: payload.roles?.[0]?.name,
        sales_work_type: payload?.sales_work_type || '',
        parent_id: payload?.parent_id || '',
        permissions: payload?.permissions,
        office_name: payload?.office_name,
        city_name: payload?.office?.city_name ?? payload?.delivery_location?.city_name,
        country_name: payload?.office?.country_name ?? payload?.delivery_location?.country_name,
        date: payload?.created_at,
        other_documents: payload.other_docs,
        other_documents_detail: payload.other_documents_detail != null ? payload.other_documents_detail : "",
        roles: payload.roles,
      };
    },
    resetStatus(state) {
      state.status = "idle";
      state.error = null;
      state.loading = false;
      state.list_loading = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRole.fulfilled, (state, action) => {
        state.roles = action.payload;
      })
      .addCase(getManager.fulfilled, (state, action) => {
        state.managers = action.payload;
      })
      .addCase(fetchRolePermission.fulfilled, (state, action) => {
        state.role_permissions = action.payload;
      })
      .addCase(fetchCountryMobileCode.fulfilled, (state, action) => {
        state.mobile_country_code = action.payload;
      }).addCase(fetchCountry.fulfilled, (state, action) => {
        state.countries = action.payload;
      })
      .addCase(fetchCity.fulfilled, (state, action) => {
        state.cities = action.payload;
      })
      .addCase(fetchOffice.fulfilled, (state, action) => {
        state.offices = action.payload;
      })
      .addCase(fetchOfficeCity.fulfilled, (state, action) => {
        state.offices_city = action.payload;
      })
      .addCase(addInternalUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(addInternalUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addInternalUser.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(storeDeliveryPartnerDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(storeDeliveryPartnerDetails.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(storeDeliveryPartnerDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateDeliveryPartnerDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDeliveryPartnerDetails.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateDeliveryPartnerDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getInternalUser.pending, (state) => {
        state.list_loading = true;
      })
      .addCase(getInternalUser.fulfilled, (state, action) => {
        state.list_loading = false;
        state.list_users = action.payload;
      })
      .addCase(getInternalUser.rejected, (state, action) => {
        state.list_loading = false;
      })
      .addCase(deleteInternalUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteInternalUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteInternalUser.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(approveInternalUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveInternalUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(approveInternalUser.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(bulkActionUpdate.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkActionUpdate.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(bulkActionUpdate.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(sendUserLoginDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendUserLoginDetails.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(sendUserLoginDetails.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(fetchDeliveryPartnerCities.fulfilled, (state, action) => {
        state.delivery_partner_cities = action.payload;
      })
      .addCase(fetchDeliveryPartnerOrders.pending, (state) => {
        state.list_loading = true;
      })
      .addCase(fetchDeliveryPartnerOrders.fulfilled, (state, action) => {
        state.list_loading = false;

        const payload = action.payload?.data || {};

        state.delivery_orders = payload.orders || [];
        state.delivery_orders_meta = payload.pagination || null;

        state.delivery_summary = {
          payment_pending: payload.payment_pending,
          deduction_amount: payload.deduction_amount,
          completed_jobs: payload.completed_jobs,
          pending_jobs: payload.pending_jobs,
          progress_percentage: payload.progress_percentage,
          total_earned: payload.total_earned,
        };
      })
      .addCase(fetchDeliveryPartnerOrders.rejected, (state) => {
        state.list_loading = false;
      })
      // Download Internal User Report
      .addCase(downloadInternalUserReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(downloadInternalUserReport.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(downloadInternalUserReport.rejected, (state, action) => {
        state.loading = false;
      })
  }
});

export const { setSelectedInternalUser, resetStatus } = internalUsersSlice.actions;
export default internalUsersSlice.reducer;
